import React from 'react';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withState } from 'recompose';
import { withDebouncedHandlers } from '../../../helpers/hocs/with-debounced-handlers';
import { confirmSignUp, resendCodeSignUp, setAccountData } from '../../../store/reducers/account/actions';
import { InputButtonWithIcon as ButtonWithIcon } from '../../../components/buttons/button-with-icon';
import { InputField as Input } from '../../../components/inputs/input';
import { AccountPage } from '../../../templates/account';
import { UserService } from '../../../services/user-service';
import { toHistoryArgs, withSearchParams } from '../../../helpers/navigation/routing';
import { withCompletedPrevStep, withUser } from '../components';
import { WrongMagicCodeModal } from '../components/modals';

const Component = ({
  value,
  isValid,
  error,
  handleChange,
  handleSubmit,
  handleNewCodeClick,
  resending,
  showModal,
  toggleModal
}) => {
  const {
    ACCOUNT: {
      SIGN_UP: {
        title,
        identifier,
        subheading,
        pageHeader,
        magicCode: {
          label,
          inPageMessage,
          message,
          heading,
          body,
          legalFooter,
          buttons: { confirm, requestNewCode }
        }
      }
    }
  } = window.CONFIG;

  const hide = () => toggleModal(false);

  return (
    <AccountPage
      title={title}
      subheading={subheading}
      category={identifier}
      inPageMessage={inPageMessage}
      message={message}
      heading={heading}
      body={body}
      legalFooter={legalFooter}
      modal={<WrongMagicCodeModal show={showModal} onClose={hide} />}
      onSubmit={handleSubmit}
      pageHeader={pageHeader}
    >
      <Input
        data-atid='sign-up-magic-code-input'
        type='text'
        label={label}
        onChange={handleChange}
        value={value}
        error={error}
      />
      <ButtonWithIcon data-atid='sign-up-submit-button' buttonType='submit' disabled={!isValid}>
        {confirm}
      </ButtonWithIcon>
      <ButtonWithIcon
        data-atid='sign-up-request-new-code-button'
        disabled={resending}
        type='minor'
        onClick={handleNewCodeClick}
      >
        {requestNewCode}
      </ButtonWithIcon>
    </AccountPage>
  );
};

const mapStateToProps = ({ account, marketingLists }) => {
  const value = account.code;

  return {
    value,
    error: account.confirmingError,
    resending: account.resendingCodeSignUp,
    isValid: String(value).length > 0,
    marketingLists: marketingLists.lists
  };
};

export const SignUpMagicCode = compose(
  withUser(),
  withCompletedPrevStep('/account/sign-up'),
  connect(mapStateToProps, { setAccountData, confirmSignUp, resendCodeSignUp }),
  withState('showModal', 'toggleModal', false),
  withHandlers({
    handleChange:
      ({ setAccountData }) =>
      value =>
        setAccountData('code', value),
    handleNewCodeClick:
      ({ resendCodeSignUp }) =>
      () =>
        resendCodeSignUp()
  }),
  withDebouncedHandlers({
    handleSubmit:
      ({ confirmSignUp, history, marketingLists, toggleModal }) =>
      () => {
        const nextPage =
          marketingLists.length && !UserService.hasAccount()
            ? toHistoryArgs(withSearchParams('/account/sign-up/marketing'), { completed: true })
            : withSearchParams('/account/sign-up/account-ready');

        confirmSignUp(
          () => history.push(nextPage),
          () => toggleModal(true)
        );
      }
  }),
  lifecycle({
    componentDidMount() {
      const { setAccountData } = this.props;

      setAccountData('code', '');
      setAccountData('confirmingError', '');
      setAccountData('resendingCodeSignUpError', '');
    }
  })
)(Component);
